import { Box, BoxProps, Flex } from '@chakra-ui/react';
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react';
import { CSSProperties, memo, RefObject, useCallback, useDeferredValue, useState } from 'react';

import EscapableInput from '@components/common/EscapableInput';
import OrgCreateMenu from '@features/onboarding/OrgCreateMenu';
import { stopEventPropagation } from 'helpers/browserEvent';
import { useBoolean } from 'hooks/useBoolean';
import useOnClickOutside from 'hooks/useOnClickOutside';

interface Props {
  setFloatingReference: (node: HTMLElement | null) => void;
  floatingStyles: CSSProperties;
  onCancel: () => void;
  onCreateOrg: (name: string, slug: string) => void;
  width?: BoxProps['width'];
  inputHeight?: BoxProps['height'];
}

const CreateOrgPopover = ({
  setFloatingReference,
  floatingStyles,
  onCancel,
  onCreateOrg,
  inputHeight,
}: Props) => {
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useBoolean();
  const [orgName, setOrgName] = useState('');
  const deferredOrgName = useDeferredValue(orgName);

  const { refs, floatingStyles: menuFloatingStyles } = useFloating({
    open: isCreateMenuOpen,
    placement: 'bottom-start',
    onOpenChange: setIsCreateMenuOpen.toggle,
    middleware: [offset(8), flip()],
    whileElementsMounted: autoUpdate,
  });

  useOnClickOutside([refs.floating, refs.reference as RefObject<HTMLElement | null>], onCancel);

  const onOrgNameChange = useCallback(
    (value: string) => {
      setOrgName(value);
      setIsCreateMenuOpen.on();
    },
    [setIsCreateMenuOpen],
  );

  const onSave = useCallback(() => {
    onCreateOrg(orgName, orgName);
  }, [orgName, onCreateOrg]);

  return (
    <Box
      zIndex="popover"
      bg="surface.background"
      boxShadow="sm"
      borderRadius="md"
      ref={setFloatingReference}
      style={floatingStyles}
    >
      <Flex
        data-testid="org-create-menu"
        flexDir="column"
        columnGap={1}
        rowGap={0}
        onClick={stopEventPropagation}
      >
        <EscapableInput
          data-testid="org-create-menu-input"
          ref={refs.setReference}
          name="orgName"
          border="none"
          autoFocus
          borderRadius="md"
          value={orgName}
          onChange={onOrgNameChange}
          fontSize="xs"
          fontWeight="medium"
          onCancel={onCancel}
          saveOnEnter
          // Since the input shape is weird, it's easy to accidentally blur when we don't want it saving
          // handle saving on blur in a parent component
          saveOnBlur={false}
          selectOnFocus={false}
          size="sm"
          variant="subtle"
          height={inputHeight ?? '42px'}
          overflow="hidden"
          py={2}
          px={3}
          placeholder="Type name of business to create"
          boxShadow="default"
          width="full"
          onSave={onSave}
        />
      </Flex>
      {isCreateMenuOpen && deferredOrgName.length > 0 && (
        <OrgCreateMenu
          setFloatingReference={refs.setFloating}
          floatingStyles={menuFloatingStyles}
          deferredOrgName={deferredOrgName}
          onSave={onSave}
        />
      )}
    </Box>
  );
};

export default memo(CreateOrgPopover);
